import React from 'react'
import { Card, Col, Modal, Row, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { parseDate } from '../../../config/dayjsConfig'
import { Match, ReconciliationCounterpartyGroup } from '../types'
import {
  buildCounterpartyGroups,
  formatAmountWithCurrency,
  getClassifications,
  getCompanyName,
  getCounterpartyName,
  getDifference,
  getReference
} from '../utils/reconciliationHelpers'
import { ClassificationTags } from './ClassificationTag'
import ReconciliationVoucherGroups from './ReconciliationVoucherGroups'

const { Title, Text } = Typography

const formatDate = (value?: string) => (value ? parseDate(value)?.format('DD.MM.YYYY') ?? value : value)

interface ReconciliationEntryModalProps {
  match: Match | null
  companyNameById: Map<string, string>
  // ISO code of the group's reporting currency (parent company's `currency`).
  groupCurrency: string
  onClose: () => void
}

// Only shown when the entry actually has a difference, so the pill is always the "mismatch" style.
const DifferenceBadgeRow = styled.div`
  text-align: right;
  margin-bottom: 12px;
`

const DifferenceBadge = styled.span`
  display: inline-block;
  font: 600 10px ui-monospace, Menlo, monospace;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid #f5222d;
  background: #fff1f0;
  color: #cf1322;
`

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
    <Text type="secondary">{label}:</Text>
    <Text>{value}</Text>
  </div>
)

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#9a948a',
      marginBottom: 6
    }}
  >
    {children}
  </div>
)

const GroupInfoCard: React.FC<{ group: ReconciliationCounterpartyGroup; groupCurrency: string }> = ({
  group,
  groupCurrency
}) => {
  const { t } = useTranslation()
  const { voucher } = group
  const { invoice } = voucher
  const showLedger = !!voucher.ledgerCurrency && voucher.ledgerCurrency !== groupCurrency

  return (
    <Card
      size="small"
      title={group.companyName}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{
        header: { background: '#fafafa' },
        body: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* Tosite grows so the Lasku block sits flush to the card bottom, aligned across both sides. */}
      <div style={{ padding: '12px 16px', flex: 1 }}>
        <SectionLabel>{t('icReconciliation:modalVoucherSection', 'Tosite')}</SectionLabel>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <InfoRow
            label={t('icReconciliation:modalType', 'Tyyppi')}
            value={t(`icReconciliation:voucherType-${voucher.type}`, voucher.type)}
          />
          <InfoRow label={t('icReconciliation:modalDate', 'Päivä')} value={formatDate(voucher.date)} />
          <InfoRow
            label={t('icReconciliation:modalDebit', 'Debet')}
            value={formatAmountWithCurrency(voucher.debit, groupCurrency)}
          />
          <InfoRow
            label={t('icReconciliation:modalCredit', 'Kredit')}
            value={formatAmountWithCurrency(voucher.credit, groupCurrency)}
          />
          {showLedger && (
            <>
              <InfoRow
                label={t('icReconciliation:modalLedgerDebit', 'Debet (kirjausvaluutta)')}
                value={formatAmountWithCurrency(voucher.ledgerDebit, voucher.ledgerCurrency ?? '')}
              />
              <InfoRow
                label={t('icReconciliation:modalLedgerCredit', 'Kredit (kirjausvaluutta)')}
                value={formatAmountWithCurrency(voucher.ledgerCredit, voucher.ledgerCurrency ?? '')}
              />
            </>
          )}
          <InfoRow label={t('icReconciliation:modalDescription', 'Kuvaus')} value={voucher.description} />
        </Space>
      </div>
      {invoice && (
        <div style={{ padding: '12px 16px', borderTop: '1px dashed #e3dfd6', background: '#fbfaf7' }}>
          <SectionLabel>{t('icReconciliation:modalInvoiceSection', 'Lasku')}</SectionLabel>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <InfoRow label={t('icReconciliation:modalInvoiceNumber', 'Numero')} value={invoice.number} />
            <InfoRow label={t('icReconciliation:tableReference', 'Viite')} value={invoice.referenceNumber} />
            <InfoRow label={t('icReconciliation:modalCurrency', 'Valuutta')} value={invoice.currency} />
            <InfoRow label={t('icReconciliation:modalInvoiceDate', 'Laskun pvm')} value={formatDate(invoice.date)} />
            <InfoRow label={t('icReconciliation:modalDueDate', 'Eräpäivä')} value={formatDate(invoice.dueDate)} />
            <InfoRow
              label={t('icReconciliation:modalPaymentDate', 'Maksupäivä')}
              value={formatDate(invoice.paymentDate) ?? '—'}
            />
            <InfoRow
              label={t('icReconciliation:modalPaid', 'Maksettu')}
              value={invoice.paid ? t('global:yes', 'Kyllä') : t('global:no', 'Ei')}
            />
            <InfoRow label={t('icReconciliation:modalPartner', 'Partneri')} value={invoice.partner?.name} />
          </Space>
        </div>
      )}
    </Card>
  )
}

const EmptySideCard: React.FC<{ label: string; message: string }> = ({ label, message }) => (
  <Card
    size="small"
    title={label || undefined}
    style={{ height: '100%', borderStyle: 'dashed' }}
    styles={{
      header: { background: '#fafafa' },
      body: { height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    }}
  >
    <Text type="secondary">{message}</Text>
  </Card>
)

const ReconciliationEntryModal: React.FC<ReconciliationEntryModalProps> = ({
  match,
  companyNameById,
  groupCurrency,
  onClose
}) => {
  const { t } = useTranslation()
  const groups = match ? buildCounterpartyGroups(match, companyNameById) : []
  const difference = match ? getDifference(match) : 0
  const ownerName = match ? getCompanyName(match, companyNameById) : ''
  const counterpartyName = match ? getCounterpartyName(match, companyNameById) : ''

  return (
    <Modal
      open={!!match}
      onCancel={onClose}
      footer={null}
      width={960}
      title={t('icReconciliation:modalTitle', 'Tapahtuman tiedot')}
    >
      {match && (
        <>
          {difference !== 0 && (
            <DifferenceBadgeRow>
              <DifferenceBadge>
                {t('icReconciliation:tableDifference', 'Erotus').toUpperCase()}{' '}
                {formatAmountWithCurrency(difference, groupCurrency)}
              </DifferenceBadge>
            </DifferenceBadgeRow>
          )}
          <Row gutter={16} align="stretch">
            <Col span={12}>
              {match.a ? (
                <GroupInfoCard group={{ companyName: ownerName, voucher: match.a }} groupCurrency={groupCurrency} />
              ) : (
                <EmptySideCard
                  label={ownerName}
                  message={t('icReconciliation:modalNoSalesVoucher', 'Ei myyntitositetta')}
                />
              )}
            </Col>
            <Col span={12}>
              {match.b ? (
                <GroupInfoCard
                  group={{ companyName: counterpartyName, voucher: match.b }}
                  groupCurrency={groupCurrency}
                />
              ) : (
                <EmptySideCard
                  label={counterpartyName}
                  message={t('icReconciliation:modalNoPurchaseVoucher', 'Ei ostotositetta')}
                />
              )}
            </Col>
          </Row>
          <div style={{ marginTop: 24 }}>
            <Title level={5}>{t('icReconciliation:tableClassification', 'Luokitus')}</Title>
            <ClassificationTags classifications={getClassifications(match)} />
          </div>
          <div style={{ marginTop: 24 }}>
            <Title level={5}>{t('icReconciliation:modalTransactions', 'Tositteen vientirivit')}</Title>
            <ReconciliationVoucherGroups groups={groups} reference={getReference(match)} />
          </div>
        </>
      )}
    </Modal>
  )
}

export default ReconciliationEntryModal
